import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface ClientLogo {
  id: string;
  brand_id: string;
  gallery_id: string;
  name: string;
  logo_url: string;
  website?: string;
  alt_text?: string;
  display_order: number;
  is_published: boolean;
  created_at: string;
  updated_at: string;
}

export function useClientLogos(brandId: string | undefined, galleryId?: string) {
  const queryClient = useQueryClient();

  const { data: logos, isLoading } = useQuery({
    queryKey: ['client-logos', brandId, galleryId],
    queryFn: async () => {
      if (!brandId) return [];
      
      let query = supabase
        .from('brand_client_logos')
        .select('*')
        .eq('brand_id', brandId);

      if (galleryId) {
        query = query.eq('gallery_id', galleryId);
      }

      const { data, error } = await query
        .order('display_order', { ascending: true })
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as ClientLogo[];
    },
    enabled: !!brandId,
  });

  const createMutation = useMutation({
    mutationFn: async (logo: Omit<ClientLogo, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('brand_client_logos')
        .insert([logo])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-logos', brandId] });
      toast.success('Logo created successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to create logo: ${error.message}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<ClientLogo> & { id: string }) => {
      const { data, error } = await supabase
        .from('brand_client_logos')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-logos', brandId] });
      toast.success('Logo updated successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to update logo: ${error.message}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('brand_client_logos')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-logos', brandId] });
      toast.success('Logo deleted successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to delete logo: ${error.message}`);
    },
  });

  return {
    logos: logos || [],
    isLoading,
    createLogo: createMutation.mutateAsync,
    updateLogo: updateMutation.mutateAsync,
    deleteLogo: deleteMutation.mutateAsync,
  };
}
